import React from "react";
import { ActionDialog, Button } from "../../../components/ui";
import { formatPrice } from "../../brand/planFormat";

export interface DeclinePaymentModalProps {
  /** What was asked for. */
  amount: number;
  /** Who it was asked of: "Priya Sharma". */
  payer: string;
  /** What it was for: the plan name. */
  planName: string;
  /** How long that plan runs: "6 Months". */
  duration: string;
  /** Set while the cancellation is with the server. */
  busy?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * The other half of the question `ConfirmPaymentModal` asks: the request is not
 * going to be paid, and is being called off.
 *
 * Its own dialog rather than a branch in the confirm — that one asks for the
 * day the money came in, and this one asks for nothing. Shared for the same
 * reason the confirm is: a client's payment history, an enquiry row and the
 * enquiry's own screen all call off the same record, and a request cancelled
 * from one place must read as cancelled everywhere.
 *
 * A sheet up from the bottom edge on a phone, like the confirm it stands
 * opposite — see `ActionDialog`. The two are the same question answered two
 * ways, so they arrive the same way.
 */
const DeclinePaymentModal: React.FC<DeclinePaymentModalProps> = ({
  amount,
  payer,
  planName,
  duration,
  busy = false,
  onClose,
  onConfirm,
}) => (
  <ActionDialog
    // The verb, not the outcome: the panel is asking, and "Payment declined"
    // is what the toast says once it has been.
    title="Decline payment"
    onClose={() => !busy && onClose()}
    className="max-w-[440px]"
    // Not while the cancellation is with the server — see `ConfirmPaymentModal`.
    dismissable={!busy}
    footer={
      <>
        <Button variant="secondary" size="md" disabled={busy} onClick={onClose}>
          {/* "Cancel" beside a button that declines the payment would be two
              different cancellations an inch apart. */}
          Keep it
        </Button>
        <Button size="md" variant="danger" loading={busy} onClick={onConfirm}>
          Decline payment
        </Button>
      </>
    }
  >
    <p className="text-[14px] 3xl:text-[15px] text-ink-muted">
      <span className="font-medium text-ink">{payer}</span> has declined the{" "}
      <span className="font-medium text-ink">{formatPrice(amount)}</span>{" "}
      payment request for {planName} ({duration}). This request will remain
      in history as declined and cannot be undone.
    </p>
  </ActionDialog>
);

export default DeclinePaymentModal;
